/**
 * RenderTable
 *
 * Generic HTML renderer for a RenderTableModel (see src/utils/renderModel.ts).
 * Used for both the on-screen view and the print view (print is the same DOM
 * under @media print). The companion PDF renderer lives in src/utils/pdf.ts.
 *
 * Renders only the `<div class="table-responsive"><table>…</table></div>`
 * portion; section titles, icons, and surface layout stay in the parent
 * components (MaterialsList / PlacementGuide).
 */

import React from 'react';
import { RenderCell, RenderColumn, RenderCtx, RenderTableModel } from '../../utils/renderModel';
import { CheckboxChecked, CheckboxUnchecked } from '../common/CheckboxIcons';
import { trackRetailerProductClick, type ProductLinkSection } from '../../utils/gtmTracking';

interface RenderTableProps {
  table: RenderTableModel;
  ctx: RenderCtx;
}

/**
 * Map per-output visibility flags to Bootstrap display utilities so a single
 * DOM serves both browser and print. PDF visibility is handled in pdf.ts.
 * Returns null to drop the column from the DOM entirely (hidden everywhere).
 */
function visibilityClass(col: RenderColumn): string | null {
  const { visibleBrowser, visiblePrint } = col;
  if (!visibleBrowser && !visiblePrint) return null;
  if (visibleBrowser && !visiblePrint) return 'd-print-none';
  if (!visibleBrowser && visiblePrint) return 'd-none d-print-table-cell';
  return '';
}

function classNames(...parts: (string | null | undefined)[]): string | undefined {
  const joined = parts.filter(Boolean).join(' ').trim();
  return joined || undefined;
}

const CellContent: React.FC<{ cell: RenderCell; section: ProductLinkSection }> = ({ cell, section }) => {
  switch (cell.type) {
    case 'text':
      return <>{cell.text}</>;
    case 'link':
      return (
        <a
          href={cell.href}
          target="_blank"
          rel="noopener noreferrer"
          // A retailer's product link (custom field with a URL template). The
          // model attached what to report; the section comes from the table.
          onClick={cell.analytics ? () => trackRetailerProductClick(cell.analytics!, section, cell.href) : undefined}
        >
          {cell.text}
        </a>
      );
    case 'checkbox':
      return cell.checked ? <CheckboxChecked /> : <CheckboxUnchecked />;
    case 'barcode':
      // width:auto keeps the 1D aspect ratio (a fixed height + maxWidth:100%
      // squishes the bars to nothing in a narrow column).
      return (
        <img
          src={cell.src}
          alt={cell.alt}
          className="bom-barcode-img"
          style={{ height: cell.heightPx ? `${cell.heightPx + 16}px` : '46px', width: 'auto', display: 'block', margin: '0 auto' }}
        />
      );
    case 'empty':
    default:
      return null;
  }
};

export const RenderTable: React.FC<RenderTableProps> = ({ table, ctx }) => {
  // Pair each column with its footer cell BEFORE filtering so the two stay
  // aligned when a column is hidden. Keep only columns visible in browser/print.
  const cols = table.columns
    .map((col, i) => ({ col, vis: visibilityClass(col), footer: table.footer?.[i] }))
    .filter(
      (c): c is { col: RenderColumn; vis: string; footer: typeof c.footer } => c.vis !== null,
    );

  return (
    <div className="table-responsive">
      <table className={`table table-bordered table-striped ${table.tableClassName}`}>
        <thead className="table-tc-slate">
          <tr>
            {cols.map(({ col, vis }) => (
              <th key={col.key} className={classNames(col.thClassName, vis)}>
                {col.headingIcon === 'checked' ? <CheckboxChecked /> : col.heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-group-divider">
          {table.rows.map((row, rowIdx) => (
            <tr key={`${row.productID}-${rowIdx}`}>
              {cols.map(({ col, vis }) => (
                <td key={col.key} className={classNames(col.tdClassName, vis)}>
                  <CellContent cell={col.cell(row, ctx)} section={table.analyticsSection} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {table.footer && (
          <tfoot className="table-tc-footer">
            <tr>
              {cols.map(({ col, vis, footer }) => (
                <td key={col.key} className={classNames(footer?.className, vis)}>
                  {footer ? <CellContent cell={footer.cell} section={table.analyticsSection} /> : null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default RenderTable;
